<template>
  <div class="ts" data-test="task-settings">
    <SectionHead
      title="Task rules"
      :count="rules.length"
      description="Each rule creates its own task. Add as many as you need."
      :action-label="rules.length > 1 ? (allRulesOpen(rules) ? 'Collapse all' : 'Expand all') : ''"
      @action="toggleAllRules(rules)"
    />

    <RuleCard
      v-for="(rule, index) in rules"
      :key="ruleKey(rule)"
      :title="`Rule ${index + 1}`"
      :summary="taskRuleSummary(rule)"
      :empty="isRuleEmpty(rule, 'task')"
      :open="ruleOpen(rule, rules.length)"
      @toggle="toggleRule(rule, rules.length)"
      @remove="wfBuilder.removeTaskRule(index)"
    >
      <FieldRow v-if="wfBuilder.triggerRequiresProcCode" label="Proc codes">
        <WfPicker
          v-model="rule.procCodes"
          :options="wfBuilder.procCodesList"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="Select proc codes…"
          :proc-codes="true"
        />
      </FieldRow>

      <FieldRow label="Assign to">
        <WfPicker
          v-model="rule.assignees"
          :options="wfBuilder.employeesList"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="displayName"
          track-by="EmployeeID"
          placeholder="Select employees…"
        />
      </FieldRow>

      <FieldRow
        label="Description"
        :for-id="`ts-desc-${ruleKey(rule)}`"
        :note="descriptionNote"
        :note-codes="descriptionCodes"
      >
        <textarea
          :id="`ts-desc-${ruleKey(rule)}`"
          v-model="rule.description"
          class="ts__textarea"
          rows="3"
          placeholder="Enter task description"
          data-test="task-description"
        ></textarea>
      </FieldRow>

      <FieldRow label="Due date">
        <WfPicker
          v-model="rule.dueDateObj"
          :options="wfBuilder.dueDateOptions"
          :searchable="false"
          :show-labels="false"
          label="text"
          track-by="value"
          placeholder="Select due date…"
          :max-height="340"
          @input="(val) => rule.dueDate = val ? val.value : ''"
        />
      </FieldRow>

      <FieldRow label="Attach wiki">
        <WfPicker
          v-model="rule.wikis"
          :options="wfBuilder.wikisWithoutTemplates"
          :multiple="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          label="Name"
          track-by="WikiPageID"
          placeholder="Select wikis…"
        />
      </FieldRow>

      <FieldRow label="Tags">
        <WfPicker
          v-model="rule.tagsList"
          :options="wfBuilder.availableTags"
          :multiple="true"
          :taggable="true"
          :searchable="true"
          :close-on-select="false"
          :show-labels="false"
          tag-placeholder="Add tag"
          placeholder="Select or add tags…"
          @tag="(newTag) => wfBuilder.addNewTag(newTag, rule)"
        />
      </FieldRow>
    </RuleCard>

    <button type="button" class="ts__add" data-test="task-rule-add" @click="addRule">
      <WfIcon name="plus" :size="16" />
      Add task rule
    </button>
  </div>
</template>

<script>
/**
 * Assign Teamio task: one rule card per task rule (design frames 1g / 1h). EVERY field is the classic
 * builder's: the same `selectedNodeData.properties.taskRules[]` paths, option lists, `@input` side effects
 * (`dueDate` follows `dueDateObj`) and rule methods (`addTaskRule`, `removeTaskRule`, `addNewTag`).
 * There is no Card / Table switch in the redesign, so no layout state exists here.
 *
 * Proc codes show only when the trigger has them (`triggerRequiresProcCode`: Mango Voice does not), and
 * the description's hint is the classic one (the Mango placeholders, or "added automatically").
 *
 * ⛔ No prop named patNum / siteID / loading / allSettings / isSuperAdmin / userID.
 */
import FieldRow from './FieldRow.vue';
import RuleCard from './RuleCard.vue';
import SectionHead from './SectionHead.vue';
import WfPicker from './WfPicker.vue';
import WfIcon from '../WfIcon.vue';
import { ruleFold } from './ruleFold';
import { taskRuleSummary, isRuleEmpty } from '@/utils/workflow/ruleSummary';

// The placeholders a Mango Voice task description can use (the backend fills them at run time).
const MANGO_PLACEHOLDERS = ['{{callerNumber}}', '{{callerId}}', '{{callTime}}', '{{dialedNumberName}}'];

export default {
  name: 'TaskSettings',
  components: { FieldRow, RuleCard, SectionHead, WfPicker, WfIcon },
  mixins: [ruleFold],
  inject: ['wfBuilder'],

  computed: {
    p() {
      return (this.wfBuilder.selectedNodeData || {}).properties || {};
    },
    rules() {
      return this.p.taskRules || [];
    },
    descriptionNote() {
      const trigger = this.wfBuilder.currentTrigger;
      if (trigger === 'MANGO_VOICE') return 'You can use these placeholders:';
      if (trigger && !this.wfBuilder.isExternalTrigger) {
        return 'Patient name, appointment date, and procedure are added to the task automatically.';
      }
      return '';
    },
    descriptionCodes() {
      return this.wfBuilder.currentTrigger === 'MANGO_VOICE' ? MANGO_PLACEHOLDERS : [];
    },
  },

  methods: {
    taskRuleSummary,
    isRuleEmpty,
    addRule() {
      this.addRuleAndOpen(() => this.rules, () => this.wfBuilder.addTaskRule());
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../workflow-tokens';

.ts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ts__textarea {
  @include wf-textarea;
  min-height: 84px;
}

.ts__add {
  @include fu-btn(secondary, md);
  align-self: flex-start;

  @include wf-touch { height: 44px; }
}
</style>
